import type { Artist } from "../../types";
import { useEdition } from "../../hooks/useEdition";
import { useTranslations } from "../../hooks/useTranslations";
import { formatDate, formatTime, getDisplayDate } from "../../lib/dateUtils";
import { getImageUrl } from "../../lib/image";
import { getLinkIcon } from "../../lib/linkIcons";
import {
  getLocalizedCustomFields,
  getLocalizedName,
  getLocalizedValue,
} from "../../lib/localization";
import { SpotifyEmbed } from "../embeds/SpotifyEmbed";
import { YouTubeEmbed, extractYouTubeId } from "../embeds/YouTubeEmbed";
import { ImageWithSkeleton } from "../ui/ImageWithSkeleton";

type ArtistDetailProps = {
  artist: Artist;
  onEventClick: (eventId: string) => void;
};

export function ArtistDetail({ artist, onEventClick }: ArtistDetailProps) {
  const { locale, edition, timeFormat } = useEdition();
  const t = useTranslations();
  const fallback = edition?.default_locale_code;

  const spotifyId = artist.unique_artist?.spotify_id;
  const youtubeLink = artist.links.find(
    (link) => link?.url && extractYouTubeId(link.url) !== null,
  );

  const socialLinks = artist.links.filter(
    (link) => link && !(link.url && extractYouTubeId(link.url) !== null),
  );

  const imageUrl = getImageUrl(artist.image, "medium");
  const bio = getLocalizedValue(artist, "biography", locale, fallback);
  const hasMedia = !!spotifyId || !!youtubeLink;

  const spotifyOnly = spotifyId && !youtubeLink;

  const MusicIcon = (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="64"
      height="64"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M9 18V5l12-2v13" />
      <circle cx="6" cy="18" r="3" />
      <circle cx="18" cy="16" r="3" />
    </svg>
  );

  return (
    <div className="festapp-artist-detail">
      {/* Hero */}
      <div className="festapp-artist-hero">
        <h1 className="festapp-artist-hero-name">{artist.name}</h1>
        {artist.categories.length > 0 && (
          <p className="festapp-artist-hero-categories">
            {artist.categories
              .map((cat) => getLocalizedName(cat, locale, fallback))
              .filter(Boolean)
              .join(" • ")}
          </p>
        )}

        <div className="festapp-artist-hero-media">
          <div className="festapp-artist-hero-left">
            <div className="festapp-artist-hero-image-container">
              <ImageWithSkeleton
                src={imageUrl}
                alt={artist.name}
                className="festapp-artist-hero-image"
                fallbackIcon={MusicIcon}
              />
            </div>
          </div>

          {/* Media Section */}
          {hasMedia && (
            <div className="festapp-artist-hero-right">
              {spotifyId && (
                <div className="festapp-media-item">
                  <SpotifyEmbed artistId={spotifyId} tall={!!spotifyOnly} />
                </div>
              )}
              {youtubeLink && (
                <div className="festapp-media-item">
                  <YouTubeEmbed url={youtubeLink.url} />
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Schedules - moved up for prominence */}
      {artist.schedules.length > 0 && (
        <section className="festapp-artist-section">
          <h2 className="festapp-section-title">{t.schedule}</h2>
          <ul className="festapp-artist-schedules">
            {artist.schedules.map((schedule) => (
              <li key={schedule._id} className="festapp-artist-schedule-item">
                <div className="festapp-artist-schedule-date">
                  <span className="festapp-artist-schedule-day">
                    {formatDate(
                      getDisplayDate(
                        schedule.start_date,
                        schedule.start_time,
                        edition?.day_start_time ?? null,
                        edition?.start_date ?? null,
                      ),
                      "dddd, LL",
                      locale,
                    )}
                  </span>
                  {schedule.start_time && (
                    <span className="festapp-artist-schedule-time">
                      {formatTime(schedule.start_time, timeFormat)}
                      {schedule.end_time &&
                        ` – ${formatTime(schedule.end_time, timeFormat)}`}
                    </span>
                  )}
                </div>
                <div className="festapp-artist-schedule-info">
                  {schedule.place && (
                    <div className="festapp-artist-schedule-place">
                      {getLocalizedName(schedule.place, locale, fallback)}
                    </div>
                  )}
                  {schedule.event && (
                    <button
                      style={{ paddingLeft: 0 }}
                      type="button"
                      className="festapp-artist-schedule-event"
                      onClick={() => onEventClick(schedule.event!._id)}
                    >
                      {getLocalizedName(schedule.event, locale, fallback)}
                    </button>
                  )}
                </div>
                {schedule.ticket_url && (
                  <a
                    href={schedule.ticket_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="festapp-schedule-item-ticket"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M2 9a3 3 0 0 1 0 6v2a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-2a3 3 0 0 1 0-6V7a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2Z" />
                      <path d="M13 5v2" />
                      <path d="M13 17v2" />
                      <path d="M13 11v2" />
                    </svg>
                    <span>{t.getTickets}</span>
                  </a>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Bio */}
      {bio && (
        <section className="festapp-artist-section">
          <p className="festapp-artist-bio">{bio}</p>
        </section>
      )}

      {/* Social Links */}
      {socialLinks.length > 0 && (
        <section className="festapp-artist-section">
          <div className="festapp-artist-links">
            {socialLinks.map((link) => {
              const { Icon, label } = getLinkIcon(link.url);
              return (
                <a
                  key={link.url}
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="festapp-artist-link"
                  title={label}
                >
                  <Icon className="festapp-artist-link-icon" />
                </a>
              );
            })}
          </div>
        </section>
      )}

      {/* Custom Fields */}
      {artist.custom_fields &&
        artist.custom_fields.length > 0 &&
        (() => {
          const localizedCustomFields = getLocalizedCustomFields(
            artist.custom_fields,
            locale,
            fallback,
          );
          return localizedCustomFields.length > 0 ? (
            <section className="festapp-artist-section">
              <dl className="festapp-custom-fields">
                {localizedCustomFields.map((field, index) => (
                  <div
                    key={`${field.label}-${index}`}
                    className="festapp-custom-field"
                  >
                    <dt>{field.label}</dt>
                    <dd>{field.value}</dd>
                  </div>
                ))}
              </dl>
            </section>
          ) : null;
        })()}
    </div>
  );
}

